import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { apiBaseUrl } from 'App';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { AdminRefreshContext } from './AdminRefreshContext';
import { Deployment, DynoType } from './monitoringTypes';

const POLL_INTERVAL_MS = 45000;

export interface TimeRange {
    from: Date;
    to: Date;
}

export interface DynoInstancePoint {
    timestamp: string;
    dyno_id: string;
    memory_total_mb: number | null;
    memory_rss_mb: number | null;
    memory_quota_mb: number | null;
    load_avg_1m: number | null;
    load_avg_5m: number | null;
    load_avg_15m: number | null;
}

export interface ResponseTimePoint {
    timestamp: string;
    request_count: number;
    response_time_avg_ms: number | null;
    response_time_max_ms: number | null;
}

interface DynoMetricsResult {
    deployment: Deployment | null;
    instances: DynoInstancePoint[];
    response_time: ResponseTimePoint[];
}

interface DiskMetricsResult {
    deployment: Deployment | null;
    readings: { timestamp: string; size_bytes: number }[];
    quota_mb: number | null;
}

// Fetches one endpoint through a session, with logout on 403
async function fetchWithSession(apiUrl: string, params: Record<string, string>, handleLogout: () => Promise<void>) {
    return fetchData({ apiUrl, params, handleLogout });
}

// Fetches one endpoint with a viewer token, no session needed
async function fetchWithToken(apiUrl: string, params: Record<string, string>) {
    const response = await axios.get(`${apiBaseUrl}/${apiUrl}/`, { params });
    return response.data;
}

// Fetches a metric endpoint and stores the result, refetching on
// the shared refresh nonce and only polling when the context of
// the tv view enables it
export function usePolledMetrics<ResultType>(
    apiUrl: string,
    params: Record<string, string>,
    initialResult: ResultType,
    viewerToken?: string,
    pollIntervalMs: number = POLL_INTERVAL_MS
) {
    const { handleLogout } = useAuthContext();
    const { refreshNonce, pollingEnabled } = useContext(AdminRefreshContext);
    const [result, setResult] = useState<ResultType>(initialResult);
    const [loading, setLoading] = useState<boolean>(true);

    // Stringify the params so the effect only reruns on real changes
    const paramsKey = JSON.stringify(params);

    useEffect(() => {
        let cancelled = false;

        // Add the viewer token to the params if one is present
        const requestParams: Record<string, string> = { ...params };
        if (viewerToken) {
            requestParams.token = viewerToken;
        }

        // Fetches the endpoint and stores the result
        const fetchMetrics = async () => {
            const data = viewerToken
                ? await fetchWithToken(apiUrl, requestParams)
                : await fetchWithSession(apiUrl, requestParams, handleLogout);

            // Skip updating state if the effect was cleaned up
            if (cancelled) {
                return;
            }

            if (data) {
                setResult(data);
            }
            setLoading(false);
        };

        // Fetch immediately, then keep polling only on the tv view
        fetchMetrics();
        const interval = pollingEnabled ? setInterval(fetchMetrics, pollIntervalMs) : null;

        return () => {
            cancelled = true;
            if (interval) {
                clearInterval(interval);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, paramsKey, viewerToken, pollIntervalMs, pollingEnabled, refreshNonce]);

    return { result, loading };
}

// Polls the dyno metric endpoint for one dyno type, without a
// deployment the backend answers for the server it runs as
export function useDynoMetrics(dynoType: DynoType, range: TimeRange, viewerToken?: string, deployment?: Deployment) {
    // Build the query params, only sending a deployment when set
    const params: Record<string, string> = {
        dyno_type: dynoType,
        from: range.from.toISOString(),
        to: range.to.toISOString(),
    };
    if (deployment) {
        params.deployment = deployment;
    }

    const { result, loading } = usePolledMetrics<DynoMetricsResult>(
        'get_dyno_metrics',
        params,
        { deployment: null, instances: [], response_time: [] },
        viewerToken
    );

    return { dynoMetrics: result, loading };
}

// Polls the database disk metric endpoint, without a deployment
// the backend answers for the server it runs as
export function useDiskMetrics(range: TimeRange, viewerToken?: string, deployment?: Deployment) {
    // Build the query params, only sending a deployment when set
    const params: Record<string, string> = {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
    };
    if (deployment) {
        params.deployment = deployment;
    }

    const { result, loading } = usePolledMetrics<DiskMetricsResult>(
        'get_database_disk_metrics',
        params,
        { deployment: null, readings: [], quota_mb: null },
        viewerToken
    );

    return { diskMetrics: result, loading };
}
